import {
  Badge,
  Banner,
  BlockStack,
  Button,
  Card,
  Checkbox,
  InlineStack,
  Layout,
  Page,
  Select,
  SkeletonBodyText,
  Text,
  TextField,
} from '@shopify/polaris';
import { SearchIcon } from '@shopify/polaris-icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import agent from '../../app/api/agent';
import { LocationInventorySync } from '../../app/models/LocationInventorySync';
import { SyncLog } from '../../app/models/SyncLog';
import './SyncHealthDashboard.css';
import {
  buildSyncHealthJobs,
  groupSyncHealthJobs,
  isIssue,
  SyncHealth,
  SyncHealthJob,
} from './syncHealth';

dayjs.extend(relativeTime);

const healthLabels: Record<SyncHealth, string> = {
  healthy: 'Healthy',
  running: 'Running',
  delayed: 'Delayed',
  failed: 'Failed',
};

const HealthBadge = ({ health }: { health: SyncHealth }) => {
  if (health === 'healthy') return <Badge tone="success">Healthy</Badge>;
  if (health === 'running') return <Badge tone="info">Running</Badge>;
  if (health === 'delayed') return <Badge tone="warning">Delayed</Badge>;
  return <Badge tone="critical">Failed</Badge>;
};

const formatExactTime = (value: string | null) => value
  ? dayjs(value).format('MMM D, YYYY h:mm:ss A')
  : 'No completed run';

const formatRelativeTime = (value: string | null) => value
  ? dayjs(value).fromNow()
  : 'Never completed';

const summarizeGroup = (jobs: SyncHealthJob[]) => {
  const issueCount = jobs.filter(job => isIssue(job.health)).length;
  const runningCount = jobs.filter(job => job.health === 'running').length;

  if (issueCount) return `${jobs.length} jobs · ${issueCount} need attention`;
  if (runningCount) return `${jobs.length} jobs · ${runningCount} running`;
  return `${jobs.length} jobs · All healthy`;
};

const SyncHealthDashboard = () => {
  const [syncLogs, setSyncLogs] = useState<SyncLog[]>([]);
  const [locationSettings, setLocationSettings] = useState<LocationInventorySync[]>([]);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [testEmailNotice, setTestEmailNotice] = useState<{
    tone: 'success' | 'critical';
    message: string;
  } | null>(null);
  const [search, setSearch] = useState('');
  const [healthFilter, setHealthFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [issuesOnly, setIssuesOnly] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [expansionInitialized, setExpansionInitialized] = useState(false);

  const loadDashboard = useCallback(async () => {
    setLoadError(null);
    setIsRefreshing(true);

    try {
      const [logs, locations] = await Promise.all([
        agent.SyncLogInfo.list(),
        agent.LocationInventorySyncSettings.list(),
      ]);
      setSyncLogs(logs);
      setLocationSettings(locations);
      setLastRefreshedAt(new Date());
    } catch (error) {
      setLoadError('Sync health could not be loaded. Refresh the page to try again.');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const sendTestEmail = useCallback(async () => {
    setIsSendingTest(true);
    setTestEmailNotice(null);

    try {
      const result = await agent.SyncLogInfo.sendTestAlert();
      setTestEmailNotice({ tone: 'success', message: result.message });
    } catch (error) {
      setTestEmailNotice({
        tone: 'critical',
        message: 'The test email could not be sent. Check the API logs for the SMTP response.',
      });
    } finally {
      setIsSendingTest(false);
    }
  }, []);

  const jobs = useMemo(
    () => buildSyncHealthJobs(syncLogs, locationSettings),
    [syncLogs, locationSettings],
  );

  const allGroups = useMemo(() => groupSyncHealthJobs(jobs), [jobs]);

  useEffect(() => {
    if (expansionInitialized || !allGroups.length) return;

    const issueGroupKeys = allGroups
      .filter(group => group.jobs.some(job => isIssue(job.health)))
      .map(group => group.key);

    setExpandedGroups(new Set(issueGroupKeys));
    setExpansionInitialized(true);
  }, [allGroups, expansionInitialized]);

  const filteredJobs = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    return jobs.filter(job => {
      if (normalizedSearch && !`${job.name} ${job.category}`.toLowerCase().includes(normalizedSearch)) {
        return false;
      }
      if (healthFilter !== 'all' && job.health !== healthFilter) return false;
      if (categoryFilter !== 'all' && job.categoryKey !== categoryFilter) return false;
      if (issuesOnly && !isIssue(job.health)) return false;
      return true;
    });
  }, [jobs, search, healthFilter, categoryFilter, issuesOnly]);

  const filteredGroups = useMemo(() => groupSyncHealthJobs(filteredJobs), [filteredJobs]);

  const categoryOptions = useMemo(() => [
    { label: 'All categories', value: 'all' },
    ...allGroups.map(group => ({ label: group.name, value: group.key })),
  ], [allGroups]);

  const toggleGroup = (key: string) => {
    setExpandedGroups(current => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const resetFilters = () => {
    setSearch('');
    setHealthFilter('all');
    setCategoryFilter('all');
    setIssuesOnly(false);
  };

  if (isLoading) {
    return (
      <Page title="Sync health" fullWidth>
        <Layout>
          <Layout.Section>
            <Card><SkeletonBodyText lines={12} /></Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page
      title="Sync health"
      subtitle={lastRefreshedAt
        ? `Last refreshed ${dayjs(lastRefreshedAt).fromNow()}`
        : 'Operational view of scheduled synchronization jobs'}
      fullWidth
      primaryAction={{
        content: 'Refresh',
        onAction: loadDashboard,
        loading: isRefreshing,
      }}
      secondaryActions={[{
        content: 'Send test email',
        onAction: sendTestEmail,
        loading: isSendingTest,
      }]}
    >
      <div className="sync-health">
        <Layout>
          <Layout.Section>
            <BlockStack gap="500">
              {loadError && (
                <Banner tone="critical" title="Unable to load sync health">
                  <p>{loadError}</p>
                </Banner>
              )}

              {testEmailNotice && (
                <Banner
                  tone={testEmailNotice.tone}
                  title={testEmailNotice.tone === 'success' ? 'Test email sent' : 'Test email failed'}
                  onDismiss={() => setTestEmailNotice(null)}
                >
                  <p>{testEmailNotice.message}</p>
                </Banner>
              )}

              <BlockStack gap="300">
                <Card>
                  <BlockStack gap="300">
                    <div className="sync-health__filters">
                      <TextField
                        label="Search synchronization jobs"
                        labelHidden
                        value={search}
                        onChange={setSearch}
                        placeholder="Search jobs or categories"
                        prefix={<SearchIcon />}
                        clearButton
                        onClearButtonClick={() => setSearch('')}
                        autoComplete="off"
                      />
                      <Select
                        label="Health"
                        labelHidden
                        value={healthFilter}
                        onChange={setHealthFilter}
                        options={[
                          { label: 'All health states', value: 'all' },
                          { label: healthLabels.healthy, value: 'healthy' },
                          { label: healthLabels.running, value: 'running' },
                          { label: healthLabels.delayed, value: 'delayed' },
                          { label: healthLabels.failed, value: 'failed' },
                        ]}
                      />
                      <Select
                        label="Category"
                        labelHidden
                        value={categoryFilter}
                        onChange={setCategoryFilter}
                        options={categoryOptions}
                      />
                    </div>

                    <div className="sync-health__filter-footer">
                      <Checkbox
                        label="Issues only"
                        checked={issuesOnly}
                        onChange={setIssuesOnly}
                      />
                      <InlineStack gap="300">
                        {(search || healthFilter !== 'all' || categoryFilter !== 'all' || issuesOnly) && (
                          <Button variant="plain" onClick={resetFilters}>Clear filters</Button>
                        )}
                        <Button
                          variant="plain"
                          onClick={() => setExpandedGroups(new Set(filteredGroups.map(group => group.key)))}
                        >
                          Expand all
                        </Button>
                        <Button variant="plain" onClick={() => setExpandedGroups(new Set())}>
                          Collapse all
                        </Button>
                      </InlineStack>
                    </div>
                  </BlockStack>
                </Card>

                {filteredGroups.length ? (
                  <div className="sync-health__groups">
                    {filteredGroups.map(group => {
                      const isExpanded = expandedGroups.has(group.key);
                      const issueCount = group.jobs.filter(job => isIssue(job.health)).length;

                      return (
                        <section className="sync-health__group" key={group.key}>
                          <button
                            type="button"
                            className="sync-health__group-toggle"
                            onClick={() => toggleGroup(group.key)}
                            aria-expanded={isExpanded}
                            aria-controls={`sync-health-group-${group.key}`}
                          >
                            <span className="sync-health__group-title">
                              <span className="sync-health__chevron" aria-hidden="true">
                                {isExpanded ? '−' : '+'}
                              </span>
                              <Text as="span" variant="bodyMd" fontWeight="semibold">{group.name}</Text>
                            </span>
                            <span className="sync-health__group-summary">{summarizeGroup(group.jobs)}</span>
                            {issueCount > 0
                              ? <Badge tone="warning">{`${issueCount} issues`}</Badge>
                              : <Badge tone="success">Healthy</Badge>}
                          </button>

                          {isExpanded && (
                            <div id={`sync-health-group-${group.key}`} className="sync-health__table-scroll">
                              <table className="sync-health__table">
                                <thead>
                                  <tr>
                                    <th scope="col">Job</th>
                                    <th scope="col">Frequency</th>
                                    <th scope="col">Records</th>
                                    <th scope="col">Last run</th>
                                    <th scope="col">Duration</th>
                                    <th scope="col">Next run</th>
                                    <th scope="col">Health</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {group.jobs.map(job => (
                                    <tr key={job.id}>
                                      <td>
                                        <BlockStack gap="100">
                                          <Text as="span" variant="bodyMd" fontWeight="medium">{job.name}</Text>
                                          {job.errorMessage && (
                                            <Text as="span" variant="bodySm" tone="critical">
                                              {job.errorMessage}
                                            </Text>
                                          )}
                                        </BlockStack>
                                      </td>
                                      <td>{job.frequency}</td>
                                      <td>{job.recordsProcessed.toLocaleString()}</td>
                                      <td className="sync-health__timestamp">
                                        {formatRelativeTime(job.lastCompletedAt)}
                                        <span className="sync-health__exact-time">
                                          {formatExactTime(job.lastCompletedAt)}
                                        </span>
                                      </td>
                                      <td>{job.duration}</td>
                                      <td className="sync-health__timestamp">
                                        {job.nextRunAt ? formatRelativeTime(job.nextRunAt) : 'Not available'}
                                      </td>
                                      <td><HealthBadge health={job.health} /></td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </section>
                      );
                    })}
                  </div>
                ) : (
                  <Card>
                    <div className="sync-health__empty">
                      <BlockStack gap="200" inlineAlign="center">
                        <Text as="h3" variant="headingMd">No synchronization jobs found</Text>
                        <Text as="p" variant="bodyMd" tone="subdued">
                          Adjust or clear the filters to see more jobs.
                        </Text>
                        <Button onClick={resetFilters}>Clear filters</Button>
                      </BlockStack>
                    </div>
                  </Card>
                )}
              </BlockStack>
            </BlockStack>
          </Layout.Section>
        </Layout>
      </div>
    </Page>
  );
};

export default SyncHealthDashboard;
